<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/layouts/Popover/index'
import Button from '@/components/shared/Button.vue'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/layouts/Tabs/index'
import NotificationList from '@/components/shared/Notifications/NotificationList.vue'
import Icon from '@/components/shared/Icon.vue'
import { useRouter } from 'vue-router'
import { useNotifications, type SimpleNotification } from '@/composables/useNotifications/index'

const SEEN_IDS_KEY = 'notifications:seenUnreadIds'

function loadSeenIds(): Set<string> {
  try {
    const raw = localStorage.getItem(SEEN_IDS_KEY)
    return raw ? new Set(JSON.parse(raw)) : new Set()
  } catch {
    return new Set()
  }
}

function saveSeenIds(ids: Set<string>) {
  localStorage.setItem(SEEN_IDS_KEY, JSON.stringify([...ids]))
}

const router = useRouter()

const { notifications, unreadIds, markAllAsRead, isLoading } = useNotifications(undefined, {
  autoSelectFirst: false,
})

const hasNotifications = computed(() => notifications.value.length > 0)

const seenUnreadIds = ref(loadSeenIds())

// Badge shows when there are unread IDs the user hasn't seen yet
const hasNewNotifications = computed(() => {
  for (const id of unreadIds.value) {
    if (!seenUnreadIds.value.has(id)) return true
  }
  return false
})

const isOpen = ref(false)

// When popover opens, snapshot the current unread IDs as "seen"
watch(isOpen, (opened) => {
  if (opened) {
    // Merge current unread IDs into seen set
    for (const id of unreadIds.value) {
      seenUnreadIds.value.add(id)
    }
    // Prune seen IDs that are no longer unread (already read or deleted)
    for (const id of seenUnreadIds.value) {
      if (!unreadIds.value.has(id)) {
        seenUnreadIds.value.delete(id)
      }
    }
    saveSeenIds(seenUnreadIds.value)
  }
})

const handleViewAll = () => {
  isOpen.value = false
  router.push('/notifications')
}

const handleItemClick = (item: SimpleNotification) => {
  // Close the popover when an item is clicked
  isOpen.value = false
}
</script>

<template>
  <Popover :open="isOpen" @update:open="isOpen = !isOpen">
    <PopoverTrigger as-child>
      <button
        class="flex relative justify-center items-center size-10 rounded-full border bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-700 shadow-sm transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-gray-950 hover:bg-gray-50 dark:hover:bg-gray-800 hover:shadow-md active:bg-gray-100 dark:active:bg-gray-700 data-[state=open]:bg-gray-100 data-[state=open]:dark:bg-gray-800"
      >
        <span
          v-if="hasNewNotifications"
          class="flex absolute top-0 right-0 size-3"
        >
          <span
            class="inline-flex absolute w-full h-full rounded-full opacity-75 animate-ping bg-error-400 dark:bg-error-300"
          ></span>
          <span
            class="inline-flex relative rounded-full bg-error-500 dark:bg-error-400 size-3"
          ></span>
        </span>
        <div class="relative size-5">
          <Icon
            variant="bell"
            class="text-gray-900 size-[18px] dark:text-gray-100 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
          />
        </div>
      </button>
    </PopoverTrigger>
    <PopoverContent align="end" class="z-20 px-3 ml-2 w-96 sm:ml-0">
      <div class="flex gap-16 justify-between items-center">
        <h2 class="text-base font-medium text-gray-900 dark:text-gray-50">Notifications</h2>
        <Button type="ghost" @click="markAllAsRead">Mark all as read</Button>
      </div>
      <Tabs variant="solid" default-value="unread" class="mt-4">
        <TabsList class="grid grid-cols-2 w-full" variant="solid">
          <TabsTrigger value="unread">Unread</TabsTrigger>
          <TabsTrigger value="all">All</TabsTrigger>
        </TabsList>
        <div class="mt-2">
          <TabsContent value="unread">
            <NotificationList
              :loading="isLoading"
              :notifications="notifications.filter((n) => !n.read)"
              :item-to="(n) => ({ path: '/notifications', query: { focus: n.id } })"
              @item-click="handleItemClick"
            >
              <template #empty>
                <div class="py-6 text-center">
                  <div class="flex justify-center mb-1.5">
                    <Icon variant="bell" class="text-gray-400 size-5 dark:text-gray-500" />
                  </div>
                  <div class="text-sm text-gray-500 dark:text-gray-400">
                    No unread notifications
                  </div>
                </div>
              </template>
            </NotificationList>
          </TabsContent>
          <TabsContent value="all">
            <div class="relative">
              <NotificationList
                :loading="isLoading"
                :notifications="notifications"
                :item-to="(n) => ({ path: '/notifications', query: { focus: n.id } })"
                @item-click="handleItemClick"
              >
                <template #empty>
                  <div class="py-6 text-center">
                    <div class="flex justify-center mb-1.5">
                      <Icon variant="bell" class="text-gray-400 size-5 dark:text-gray-500" />
                    </div>
                    <div class="text-sm text-gray-500 dark:text-gray-400">You're all caught up</div>
                  </div>
                </template>
              </NotificationList>
              <div
                v-if="hasNotifications"
                class="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-b from-transparent to-white dark:to-gray-950"
                aria-hidden="true"
              />
            </div>
            <Button
              v-if="hasNotifications"
              type="secondary"
              @click="handleViewAll"
              class="mt-2 w-full"
              >View all</Button
            >
          </TabsContent>
        </div>
      </Tabs>
    </PopoverContent>
  </Popover>
</template>
